import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Profile } from '../../Types';
import {
  CoachingComment,
  CommentThread,
  CommentThreadType,
  UserRole,
  CoachingCommentsData,
} from '../../api/CoachingComments/schema';

export const DEFAULT_COACHING_COMMENT_DATA: CoachingCommentsData = {
  coachingID: '',
  noteID: '',
  commentThreads: [],
};

export const getNotificationReceiver = (
  currentThread: CommentThread,
  authorAlias: string
): string[] => {
  const santizedParticipants = Array.from(
    new Set(
      currentThread.commentThreadParticipants
        .map(participant => participant.trim())
        .filter(
          participant => participant !== '' && participant !== authorAlias
        )
    )
  );

  return santizedParticipants;
};

export const canViewThread = (
  threadType: string | CommentThreadType,
  isOm: boolean,
  isTm: boolean,
  shouldShowOMComments: boolean
): boolean => {
  if (!shouldShowOMComments && threadType === CommentThreadType.TM_OM1) {
    return false;
  }
  if (isOm) return true;
  if (isTm) return true;
  return threadType === CommentThreadType.CSA_TM;
};

export const canAddComment = (
  threadType: string | CommentThreadType,
  isOm: boolean,
  isTm: boolean,
  shouldShowOMComments: boolean
): boolean => {
  if (!shouldShowOMComments && threadType === CommentThreadType.TM_OM1) {
    return false;
  }
  if (isOm) {
    return threadType === CommentThreadType.TM_OM1;
  }
  if (isTm) {
    return (
      threadType === CommentThreadType.CSA_TM ||
      threadType === CommentThreadType.TM_OM1
    );
  }
  return threadType === CommentThreadType.CSA_TM;
};

const findNameInProfile = (alias: string, profile: Profile): string | null => {
  if (alias == profile.user.alias) return profile.user.displayName;

  for (const user of profile.managers)
    if (alias === user.alias) return user.displayName;

  for (const user of profile.proxyCoachees)
    if (alias === user.tmEntity.alias) return user.tmEntity.displayName;

  for (const user of profile.proxyCoaches)
    if (alias === user.tmEntity.alias) return user.tmEntity.displayName;

  for (const user of profile.directReports)
    if (alias === user.alias) return user.displayName;

  for (const user of profile.sharedProfileCoachees)
    if (alias === user.alias) return user.displayName;

  for (const user of profile.sharedProfileCoaches)
    if (alias === user.alias) return user.displayName;

  return null;
};

export const getFullName = (
  alias: string,
  profile: Profile,
  managerProfile?: Profile | null
): string => {
  const nameFromUserProfile = findNameInProfile(alias, profile);
  if (nameFromUserProfile) return nameFromUserProfile;

  if (managerProfile) {
    const nameFromManagerProfile = findNameInProfile(alias, managerProfile);
    if (nameFromManagerProfile) return nameFromManagerProfile;
  }

  return alias;
};

export const determineUserRole = (isOm: boolean, isTm: boolean): string => {
  if (isOm) return UserRole.OM1;
  if (isTm) return UserRole.TM;
  return UserRole.CSA;
};

export const getCommentPlaceholderMessage = (
  isOm: boolean,
  isTm: boolean,
  threadType: string | CommentThreadType,
  intl: any
): string => {
  if (isOm) {
    return threadType === CommentThreadType.TM_OM1
      ? intl.formatMessage({
          id: 'cs_perfportal_coaching_comment_modal_add_comment_for_team_manager',
          defaultMessage: 'Add a comment for the team manager',
        })
      : intl.formatMessage({
          id: 'cs_perfportal_coaching_comment_modal_add_comment_for_team_member',
          defaultMessage: 'Add a comment for the associate',
        });
  } else if (isTm) {
    return threadType === CommentThreadType.CSA_TM
      ? intl.formatMessage({
          id: 'cs_perfportal_coaching_comment_modal_add_comment_for_team_member',
          defaultMessage: 'Add a comment for the associate',
        })
      : intl.formatMessage({
          id: 'cs_perfportal_coaching_comment_modal_add_comment_for_om',
          defaultMessage: 'Add a comment for the operations manager',
        });
  } else {
    return intl.formatMessage({
      id: 'cs_perfportal_coaching_comment_modal_add_comment_for_team_manager',
      defaultMessage: 'Add a comment for the team manager',
    });
  }
};

export const getEmptyThreadMessage = (
  threadType: string | CommentThreadType,
  isOm: boolean,
  isTm: boolean
): JSX.Element => {
  if (threadType === CommentThreadType.CSA_TM) {
    if (isTm) {
      return (
        <FormattedMessage
          id='cs_perfportal_coaching_comment_modal_conversation_with_member'
          defaultMessage='Start a conversation about this coaching session with the associate'
          description='Conversation with associate'
        />
      );
    } else {
      return (
        <FormattedMessage
          id='cs_perfportal_coaching_comment_modal_conversation_with_manager_csa'
          defaultMessage='Start a conversation about this coaching session with your team manager'
          description='Conversation with team manager'
        />
      );
    }
  } else {
    if (isOm) {
      return (
        <FormattedMessage
          id='cs_perfportal_coaching_comment_modal_conversation_with_manager_om'
          defaultMessage='Start a conversation about this coaching session with the team manager'
          description='Conversation with team manager'
        />
      );
    } else {
      return (
        <FormattedMessage
          id='cs_perfportal_coaching_comment_modal_conversation_with_om'
          defaultMessage='Start a conversation about this coaching session with the operations manager'
          description='Conversation with operations manager'
        />
      );
    }
  }
};

export const getThreadTabLabel = (
  thread: CommentThread,
  isOm: boolean,
  otherParticipant: string | undefined
): JSX.Element => {
  if (thread.commentThreadType === CommentThreadType.TM_OM1) {
    if (!isOm) {
      return (
        <FormattedMessage
          id='cs_perfportal_coaching_comment_tab_tm_om1_for_tm'
          defaultMessage='OM and TM conversation'
        />
      );
    } else {
      return (
        <FormattedMessage
          id='cs_perfportal_coaching_comment_tab_tm_om1'
          defaultMessage='OM and {alias}'
          values={{
            alias: otherParticipant || '',
          }}
        />
      );
    }
  } else {
    return (
      <FormattedMessage
        id='cs_perfportal_coaching_comment_tab_csa_tm'
        defaultMessage='TM and Associate conversation'
      />
    );
  }
};

export const getSuccessToastMessage = (intl: any): string => {
  return intl.formatMessage({
    id: 'cs_perfportal_coaching_comment_send_success',
    defaultMessage: 'Comment successfully sent',
  });
};
